import { ProductsId, SearchMaskMountOptions } from '@webc/meeseeks-ui-sdk';
import {
  notFulfilledProductDependenciesSelector,
  arrivalCitySelector,
  brandNameSelector,
  departureCitySelector,
  departureDateTimestampSelector,
  featuresSelector,
  localeSelector,
  optionsSelector,
  passengerCountSelector,
  selectedProductsSelector,
  selectedReturnDateSelector,
  selectedRouteNameSelector,
  wheelchairSelectedSelector,
} from '../../selectors';
import { emitSearchForStationEvent, emitRequestSearchEvent } from 'app/events';
import {
  isSearchInvalid,
  getPayloadForSearchResultsRedirect,
  redirectToSearchResultsPage,
  mapLocationToAutocomplete,
  getCookieValueWithRecentSearches,
  RecentSearchCookie,
  setRecentSearchesCookie,
} from 'app/functions';
import { CityDetailsResult, PartialSearchMaskLocation } from 'app/models/types/store/location';
import { storeRecentCity } from 'app/store/thunk/location/storeRecentCityThunk';
import { ThunkType } from 'app/hooks/appHooks';

const DIRECTION_TO = 'to';
const DIRECTION_FROM = 'from';

export type RequestSearchType = { noRedirect?: boolean; isGlobalTripSearch?: boolean };

export const requestSearch = ({
  noRedirect,
  isGlobalTripSearch,
}: RequestSearchType): ThunkType<void> => {
  return (dispatch, getState) => {
    const state = getState();

    const options: SearchMaskMountOptions = optionsSelector(state);
    const features: { [key: string]: boolean } = featuresSelector(state);

    const departureCity: CityDetailsResult | PartialSearchMaskLocation =
      departureCitySelector(state);
    const arrivalCity: CityDetailsResult | PartialSearchMaskLocation = arrivalCitySelector(state);
    const routeName: string = selectedRouteNameSelector(state);
    const departureDate: number = departureDateTimestampSelector(state);
    const returnDate: number | null = selectedReturnDateSelector(state);
    const selectedProducts: Record<string, any> = selectedProductsSelector(state);
    const locale: string = localeSelector(state);
    const passengerCount: number = passengerCountSelector(state);
    const wheelchairsCount: number | undefined = wheelchairSelectedSelector(state);
    const notFulfilledProductDependencies: ProductsId[] =
      notFulfilledProductDependenciesSelector(state);
    const brand = brandNameSelector(state);

    if (
      isSearchInvalid({
        passengerCount,
        departureDate,
        wheelchairsCount,
        notFulfilledProductDependencies,
        departureCity: departureCity as CityDetailsResult,
        arrivalCity: arrivalCity as CityDetailsResult,
      })
    ) {
      return;
    }

    dispatch(
      storeRecentCity({
        direction: DIRECTION_FROM,
        city: mapLocationToAutocomplete(departureCity as CityDetailsResult),
      }),
    );
    dispatch(
      storeRecentCity({
        direction: DIRECTION_TO,
        city: mapLocationToAutocomplete(arrivalCity as CityDetailsResult),
      }),
    );

    const normalizedPayload = getPayloadForSearchResultsRedirect({
      departureCity,
      arrivalCity,
      departureDate,
      returnDate,
      locale,
      routeName,
      selectedProducts,
    });

    const recentSearchesCookiePayload: RecentSearchCookie = getCookieValueWithRecentSearches({
      departureCity,
      arrivalCity,
      departureDate,
      returnDate,
      products: selectedProducts,
    });

    setRecentSearchesCookie(recentSearchesCookiePayload);

    if (!!arrivalCity.stationId || !!departureCity.stationId) {
      emitSearchForStationEvent();
    }
    emitRequestSearchEvent(normalizedPayload);
    if (noRedirect) {
      return;
    }

    redirectToSearchResultsPage(normalizedPayload, options, features, brand, isGlobalTripSearch);
  };
};
